'use client';

import * as React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { Tag, Input, IconButton, Button, Tooltip } from '@/ui';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  SectionHeader,
  SectionContent,
  RightColumn,
} from './styles/SharedStyles';
import EditButton from '@components/shared/EditButton';
import {
  useAddCandidateTag,
  useRemoveCandidateTag,
} from '@lib/candidates/useTagMutations';

const TagsList = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
});

const EditList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
});

const EditRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

interface CandidateTag {
  id: number;
  name: string;
}

interface TagsProps {
  candidate: {
    uuid: string;
    tags?: CandidateTag[];
  };
}

export default function Tags({ candidate }: TagsProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  // Local, editable copies of each tag's name — renaming detaches the old tag
  // and attaches a new one by name under the hood (tags are shared recruiter
  // entities, so we never mutate a Tag record's name directly), but the input
  // itself needs to hold the in-progress text before that commits.
  const [editedNames, setEditedNames] = React.useState<Record<string, string>>({});
  const [drafts, setDrafts] = React.useState<string[]>(['']);
  const addTag = useAddCandidateTag();
  const removeTag = useRemoveCandidateTag();
  // The last name committed per tag id. `tag.name` stays stale until the
  // mutation's cache write lands, so without this an Enter press followed by
  // the input blurring would commit the same rename twice.
  const committedNames = React.useRef<Record<number, string>>({});

  const handleStartEditing = () => {
    setEditedNames(
      Object.fromEntries((candidate.tags ?? []).map((tag) => [tag.id, tag.name]))
    );
    committedNames.current = {};
    setDrafts(['']);
    setIsEditing(true);
  };

  const handleRemoveTag = (tagId: CandidateTag['id']) => {
    removeTag.mutate(
      { candidate, tagId },
      { onError: () => toast.error('Failed to remove tag') }
    );
  };

  const updateEditedName = (tagId: CandidateTag['id'], value: string) => {
    setEditedNames((current) => ({ ...current, [tagId]: value }));
  };

  const commitRename = (tag: CandidateTag) => {
    const name = editedNames[tag.id]?.trim();
    if (!name || name === tag.name || name === committedNames.current[tag.id]) return;
    committedNames.current[tag.id] = name;
    // Detach, then attach — in that order. Both mutations write the same
    // ["candidateDetails", uuid] cache entry, so firing them together lets
    // whichever responds last decide what the tag list looks like.
    removeTag.mutate(
      { candidate, tagId: tag.id },
      {
        onSuccess: () => {
          addTag.mutate(
            { candidate, name },
            { onError: () => toast.error('Failed to rename tag') }
          );
        },
        onError: () => {
          delete committedNames.current[tag.id];
          toast.error('Failed to rename tag');
        },
      }
    );
  };

  const handleRenameKeyDown = (tag: CandidateTag) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitRename(tag);
    }
  };

  const updateDraft = (index: number, value: string) => {
    setDrafts((current) => current.map((draft, i) => (i === index ? value : draft)));
  };

  const commitDraft = (index: number) => {
    const name = drafts[index]?.trim();
    if (!name) return;
    addTag.mutate(
      { candidate, name },
      {
        // Clear the row only once the tag actually exists, so a failed add
        // leaves the typed text in place to retry.
        onSuccess: () => {
          setDrafts((current) => current.map((draft, i) => (i === index ? '' : draft)));
        },
        onError: () => toast.error('Failed to add tag'),
      }
    );
  };

  const handleDraftKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraft(index);
    }
  };

  const addDraftRow = () => setDrafts((current) => [...current, '']);

  const removeDraftRow = (index: number) => {
    setDrafts((current) =>
      current.length === 1 ? [''] : current.filter((_, i) => i !== index)
    );
  };

  return (
    <Box id="candidate-section-tags">
      <SectionContent>
        {isEditing ? (
          <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
            <Button
              variant="tertiary"
              color="pink"
              size="sm"
              noPadding
              onClick={() => setIsEditing(false)}
            >
              Done
            </Button>
          </Box>
        ) : (
          <EditButton
            onEdit={handleStartEditing}
            disabled={false}
            label="Edit/add tags"
            sx={{ top: 0, right: 0 }}
          />
        )}
        <Box>
          <RightColumn>
            <SectionHeader>
              <Typography variant="h2">Tags</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Tags are visible to you and your team only and help you find and sort candidates.
              </Typography>
            </SectionHeader>
            {isEditing ? (
              <Box>
                <EditList>
                  {candidate.tags?.map((tag) => (
                    <EditRow key={tag.id}>
                      <Input
                        fullWidth
                        value={editedNames[tag.id] ?? tag.name}
                        onChange={(event) => updateEditedName(tag.id, event.target.value)}
                        onKeyDown={handleRenameKeyDown(tag)}
                        onBlur={() => commitRename(tag)}
                      />
                      <Tooltip content={`Remove ${tag.name}`}>
                        <IconButton
                          aria-label={`Remove ${tag.name}`}
                          // Without this the input blurs on mousedown and
                          // commits a rename before the click deletes the tag.
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleRemoveTag(tag.id)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </EditRow>
                  ))}
                  {drafts.map((draft, index) => (
                    <EditRow key={`draft-${index}`}>
                      <Input
                        fullWidth
                        placeholder="Add a new tag..."
                        value={draft}
                        onChange={(event) => updateDraft(index, event.target.value)}
                        onKeyDown={handleDraftKeyDown(index)}
                        onBlur={() => commitDraft(index)}
                      />
                      <Tooltip content="Remove">
                        <IconButton
                          aria-label="Remove tag input"
                          // Without this the input blurs on mousedown and
                          // creates the very draft tag being discarded.
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => removeDraftRow(index)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </EditRow>
                  ))}
                </EditList>
                <Button variant="tertiary" size="sm" noPadding startIcon={<Plus size={16} />} onClick={addDraftRow}>
                  Add another tag
                </Button>
              </Box>
            ) : (
              <TagsList>
                {candidate.tags?.map((tag) => (
                  <Tag key={tag.id}>{tag.name}</Tag>
                ))}
                {(!candidate.tags || candidate.tags.length === 0) && (
                  <Box sx={{ fontStyle: 'italic' }}>No tags added</Box>
                )}
              </TagsList>
            )}
          </RightColumn>
        </Box>
      </SectionContent>
    </Box>
  );
}
